import { WarningOutlined } from "@ant-design/icons";
import {
    Card,
    Col,
    ConfigProvider,
    Grid,
    Layout,
    Row,
    Space,
    Table,
    Tag,
    Typography,
} from "antd";
import CategoryDistribution from "./Widgets/CategoryDistribution";
import KPICards from "./Widgets/KPICards";
import NearExpiry from "./Widgets/NearExpiry";
import OrdersActivity from "./Widgets/OrdersActivity";
import OrderTrends from "./Widgets/OrderTrends";
import StockMovement from "./Widgets/StockMovement";
import { useEffect, useState } from "react";
import {
    fetchTotalStockValue,
    getCategoryDistribution,
    getItemsBelowSafetyStock,
    getOrder,
    getWarhorseCapacity,
} from "./dashboardActions";
import dayjs from "dayjs";

const { Content } = Layout;
const { Text } = Typography;
const { useBreakpoint } = Grid;

const Dashboard = () => {
    const [data, setData] = useState({
        totalStockValue: 0,
        dailyOrdersFulfilled: 0,
        dailyItemsFulfilled: 0,
        orderTrend: [],
        itemsTrend: [],
        trendCategory: [],
        warehouseUsageSeries: [],
    });
    const [stockMovement, setStockMovement] = useState(null);
    const [categoryDist, setCategoryDis] = useState([]);
    const [belowStock, setBelowStock] = useState([]);
    const [loading, setLoading] = useState({
        totalStock: true,
        fulfilled: true,
        capacity: true,
        category: true,
        belowStock: true
    });

    const screens = useBreakpoint();
    const chartHeight = screens.xs ? 200 : 350;

    const lowStockColumns = [
        {
            title: 'Code',
            dataIndex: 'code',
            key: 'code',
        },
        {
            title: "Name",
            dataIndex: "name",
            key: "name",
            sorter: (a, b) => a.name.localeCompare(b.name),
            render: (text) => (
                <Text strong ellipsis={{ tooltip: text }}>
                    {text}
                </Text>
            ),
        },
        {
            title: "Safety Stock Level",
            dataIndex: "safety_stock_level",
            key: "safety_stock_level",
            align: "center",
            width: "150px",
            sorter: (a, b) => a.safety_stock_level - b.safety_stock_level,
            render: (value) => <Text strong>{value}</Text>,
        },
        {
            title: "Current Quantity",
            dataIndex: "stock_level",
            key: "stock_level",
            align: "center",
            width: "150px",
            sorter: (a, b) => a.stock_level - b.stock_level,
            render: (value) =>
                value === 0 ? (
                    <Tag color="red">Out of Stock</Tag>
                ) : (
                    <Text strong>
                        {value}
                    </Text>
                ),
        },
    ];

    useEffect(() => {
        fetchTotalStockValue().then((data) => {
            if (data?.ack) {
                setData((prev) => ({
                    ...prev,
                    totalStockValue: data?.total_stock_value[0]?.total_value,
                }));
            }
        }).finally(() => {
            setLoading(prev => ({ ...prev, totalStock: false }));
        });;

        getWarhorseCapacity().then((data) => {
            if (data?.ack) {
                const validData = data?.warehouse_capacity
                    .filter(item => item.category_type !== null)
                    .sort((a, b) => a.category_type - b.category_type);
                const used = [];
                const free = [];

                validData.forEach((item) => {
                    const capacity = parseFloat(item.pallet_capacity);
                    const remaining = parseFloat(item.remaining_capacity);
                    const usedPercent = Math.round(((capacity - remaining) / capacity) * 100);
                    const freePercent = 100 - usedPercent;

                    used.push(Number(usedPercent.toFixed(2)));
                    free.push(Number(freePercent.toFixed(2)));
                });

                const warehouseUsageSeries = [
                    { name: 'Used', data: used },
                    { name: 'Free', data: free }
                ];

                setData((prev) => ({
                    ...prev,
                    warehouseUsageSeries,
                }));

            }
        }).finally(() => {
            setLoading(prev => ({ ...prev, capacity: false }));
        });

        getOrder().then((data) => {
            if (data?.ack) {
                const fulfilledOrder = data?.sale_quantities?.at(-1);
                const orderTrend = data?.sale_quantities?.map(
                    (item) => item.total_orders
                );
                const trendCategory = data?.sale_quantities?.map((item) =>
                    dayjs(item?.posting_date).format("DD MMM")
                );

                const itemsTrend = data?.sale_quantities?.map(
                    (item) => item.total_quantity
                );

                setData((prev) => ({
                    ...prev,
                    dailyOrdersFulfilled: fulfilledOrder?.total_orders || 0,
                    dailyItemsFulfilled: fulfilledOrder?.total_quantity || 0,
                    orderTrend: orderTrend || [],
                    itemsTrend: itemsTrend || [],
                    trendCategory: trendCategory || [],
                }));

                const stockIn = data?.purchase_quantities || [];
                const stockOut = data?.sale_quantities || [];

                const allDatesSet = new Set([
                    ...stockIn?.map((item) => item.received_date),
                    ...stockOut?.map((item) => item.posting_date),
                ]);

                const allDatesSorted = Array.from(allDatesSet).sort();

                const stockInMap = Object.fromEntries(
                    stockIn.map((item) => [item.received_date, item.total_quantity])
                );
                const stockOutMap = Object.fromEntries(
                    stockOut.map((item) => [item.posting_date, item.total_quantity])
                );

                const categories = allDatesSorted.map((date) =>
                    dayjs(date).format("ddd")
                );
                const stockInData = allDatesSorted.map((date) => stockInMap[date] || 0);
                const stockOutData = allDatesSorted.map(
                    (date) => stockOutMap[date] || 0
                );

                setStockMovement({
                    categories,
                    stockInData,
                    stockOutData,
                });
            }
        }).finally(() => {
            setLoading(prev => ({ ...prev, fulfilled: false }));
        });

        getCategoryDistribution().then((response) => {
            if (response?.ack) {
                const category = response?.category_distribution.map(
                    (item) => item?.key_category || "Others"
                );
                const series = response?.category_distribution.map(
                    (item) => item?.total_value || 0
                );

                setCategoryDis({
                    category,
                    series
                });
            }
        }).finally(() => {
            setLoading(prev => ({ ...prev, category: false }));
        });

        getItemsBelowSafetyStock().then(response => {
            if (response?.ack) {
                setBelowStock(response?.items_below_safety_stock)
            }
        }).finally(() => {
            setLoading(prev => ({ ...prev, belowStock: false }));
        })
    }, []);

    return (
        <Content>
            {/* Enhanced KPI Cards with Mini Charts */}
            <KPICards data={data} loading={loading} />

            {/* Main Charts Section - Row 1 */}
            <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
                <Col xs={24} lg={14}>
                    <StockMovement height={chartHeight} data={stockMovement} loading={loading.fulfilled} />
                </Col>

                <Col xs={24} lg={10}>
                    <CategoryDistribution height={chartHeight} data={categoryDist} loading={loading.category} />
                </Col>
            </Row>

            {/* Main Charts Section - Row 2 */}
            <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
                <Col xs={24} lg={12}>
                    <OrderTrends height={chartHeight} />
                </Col>

                <Col xs={24} lg={12}>
                    <NearExpiry />
                </Col>
            </Row>

            {/* Compact Exception Tables */}
            <Row gutter={[24, 24]}>
                <Col xs={24} lg={10}>
                    <Card
                        title={
                            <Space>
                                <WarningOutlined style={{ color: "#ff5722" }} />
                                <span style={{ fontWeight: 600 }}>
                                    Items Below Safety Stock Level
                                </span>
                            </Space>
                        }
                        styles={{
                            header: { background: '#f5f5f5' },
                            body: { padding: '0' }
                        }}
                        style={{
                            borderRadius: "12px",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                            border: "1px solid #f0f0f0",
                            height: "100%",
                        }}
                    >
                        <ConfigProvider
                            theme={{
                                components: {
                                    Table: {
                                        headerBorderRadius: 0,
                                    }
                                }
                            }}
                        >
                            <Table
                                loading={loading?.belowStock}
                                columns={lowStockColumns}
                                dataSource={belowStock}
                                pagination={false}
                                size="small"
                                scroll={{ x: "max-content", y: 380 }}
                                rowClassName={(_, index) => (index % 2 === 1 ? "table-row-striped" : "")}
                            />
                        </ConfigProvider>
                    </Card>
                </Col>
                <Col xs={24} lg={14}>
                    <OrdersActivity height={chartHeight} />
                </Col>
            </Row>
        </Content>
    );
};

export default Dashboard;